// src/pages/finance-portal/tax-doc-organizer/TaxDocOrganizer.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, MenuItem, Grid,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    IconButton, Tooltip, LinearProgress,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import axiosInstance from '../../../utils/axiosInstance';
import { useSnackbar } from 'notistack';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    NEEDED: { bg: '#fef3c7', color: '#92400e' },
    UPLOADED: { bg: '#dbeafe', color: '#1e40af' },
    REVIEWED: { bg: '#ede9fe', color: '#6d28d9' },
    APPROVED: { bg: '#dcfce7', color: '#166534' },
};

const CATEGORIES = [
    { value: '', label: 'All Categories' },
    { value: 'INCOME', label: 'Income' },
    { value: 'EXPENSE', label: 'Expense' },
    { value: 'DEDUCTION', label: 'Deduction' },
    { value: 'CREDIT', label: 'Credit' },
    { value: 'ASSET', label: 'Asset' },
    { value: 'LIABILITY', label: 'Liability' },
];

const CATEGORY_COLORS = {
    INCOME: { bg: '#dcfce7', color: '#166534' },
    EXPENSE: { bg: '#fecaca', color: '#991b1b' },
    DEDUCTION: { bg: '#dbeafe', color: '#1e40af' },
    CREDIT: { bg: '#ede9fe', color: '#6d28d9' },
    ASSET: { bg: '#fef3c7', color: '#92400e' },
    LIABILITY: { bg: '#fed7aa', color: '#9a3412' },
    OTHER: { bg: '#f1f5f9', color: '#475569' },
};

const STATUSES = [
    { value: '', label: 'All Statuses' },
    { value: 'NEEDED', label: 'Needed' },
    { value: 'UPLOADED', label: 'Uploaded' },
    { value: 'REVIEWED', label: 'Reviewed' },
    { value: 'APPROVED', label: 'Approved' },
];

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getStatusLabel(status) {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const headCellSx = {
    fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
    textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
};

const bodyCellSx = {
    fontFamily, fontSize: '0.85rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9',
};

export default function TaxDocOrganizer() {
    const { enqueueSnackbar } = useSnackbar();
    const [documents, setDocuments] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [summary, setSummary] = useState({ total: 0, uploaded: 0, reviewed: 0, approved: 0, completion: 0, by_category: [] });

    // Filters
    const [taxYear, setTaxYear] = useState(new Date().getFullYear().toString());
    const [category, setCategory] = useState('');
    const [status, setStatus] = useState('');

    // Add/Edit Document Dialog
    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        document_name: '', category: 'INCOME', description: '',
        file_name: '', document_url: '', due_date: '',
    });
    const [saving, setSaving] = useState(false);

    // Review Dialog
    const [reviewOpen, setReviewOpen] = useState(false);
    const [reviewDocId, setReviewDocId] = useState(null);
    const [reviewerNotes, setReviewerNotes] = useState('');
    const [reviewing, setReviewing] = useState(false);

    // Generate Checklist Dialog
    const [checklistOpen, setChecklistOpen] = useState(false);
    const [checklistYear, setChecklistYear] = useState(new Date().getFullYear().toString());
    const [generating, setGenerating] = useState(false);

    const fetchDocuments = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (taxYear) params.tax_year = taxYear;
            if (category) params.category = category;
            if (status) params.status = status;
            const res = await axiosInstance.get('/api/finance/tax/documents', { params });
            if (res.data.success) {
                setDocuments(res.data.data || []);
                setTotal(res.data.total || 0);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load documents');
        } finally {
            setIsLoading(false);
        }
    }, [page, rowsPerPage, taxYear, category, status]);

    const fetchSummary = useCallback(async () => {
        try {
            const params = {};
            if (taxYear) params.tax_year = taxYear;
            const res = await axiosInstance.get('/api/finance/tax/documents/checklist-summary', { params });
            if (res.data.success) {
                const d = res.data.data || {};
                const t = d.total || 0;
                const uploaded = d.uploaded || 0;
                const reviewed = d.reviewed || 0;
                const approved = d.approved || 0;
                const completion = t > 0 ? Math.round(((uploaded + reviewed + approved) / t) * 100) : 0;
                setSummary({ total: t, uploaded, reviewed, approved, completion, by_category: d.by_category || [] });
            }
        } catch { /* silent */ }
    }, [taxYear]);

    useEffect(() => { fetchDocuments(); fetchSummary(); }, [fetchDocuments, fetchSummary]);

    const openAddForm = () => {
        setEditingId(null);
        setFormData({ document_name: '', category: 'INCOME', description: '', file_name: '', document_url: '', due_date: '' });
        setFormOpen(true);
    };

    const openEditForm = (doc) => {
        setEditingId(doc.id);
        setFormData({
            document_name: doc.document_name || '',
            category: doc.category || 'INCOME',
            description: doc.description || '',
            file_name: doc.file_name || '',
            document_url: doc.document_url || '',
            due_date: doc.due_date ? doc.due_date.split('T')[0] : '',
        });
        setFormOpen(true);
    };

    const handleSaveDocument = async () => {
        setSaving(true);
        try {
            const payload = { ...formData, tax_year: parseInt(taxYear, 10) || new Date().getFullYear() };
            if (editingId) {
                await axiosInstance.put(`/api/finance/tax/documents/${editingId}`, payload);
                enqueueSnackbar('Document updated', { variant: 'success' });
            } else {
                await axiosInstance.post('/api/finance/tax/documents', payload);
                enqueueSnackbar('Document added', { variant: 'success' });
            }
            setFormOpen(false);
            fetchDocuments();
            fetchSummary();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to save document', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this document?')) return;
        try {
            await axiosInstance.delete(`/api/finance/tax/documents/${id}`);
            enqueueSnackbar('Document deleted', { variant: 'success' });
            fetchDocuments();
            fetchSummary();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to delete document', { variant: 'error' });
        }
    };

    const openReview = (doc) => {
        setReviewDocId(doc.id);
        setReviewerNotes('');
        setReviewOpen(true);
    };

    const handleReview = async () => {
        setReviewing(true);
        try {
            await axiosInstance.post(`/api/finance/tax/documents/${reviewDocId}/review`, { reviewer_notes: reviewerNotes });
            enqueueSnackbar('Document reviewed', { variant: 'success' });
            setReviewOpen(false);
            fetchDocuments();
            fetchSummary();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to review document', { variant: 'error' });
        } finally {
            setReviewing(false);
        }
    };

    const handleGenerateChecklist = async () => {
        setGenerating(true);
        try {
            await axiosInstance.post('/api/finance/tax/documents/generate-checklist', { tax_year: parseInt(checklistYear, 10) });
            enqueueSnackbar('Checklist generated', { variant: 'success' });
            setChecklistOpen(false);
            fetchDocuments();
            fetchSummary();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to generate checklist', { variant: 'error' });
        } finally {
            setGenerating(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FolderIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Tax Document Organizer</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Organize and track tax documents by year</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button
                            variant="contained"
                            startIcon={<PlaylistAddCheckIcon />}
                            onClick={() => setChecklistOpen(true)}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            Generate Default Checklist
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={openAddForm}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'white', color: ACCENT,
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            }}
                        >
                            Add Document
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Summary Bar */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3, p: 2.5 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6} sm={2}>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Total Needed</Typography>
                            <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 800, color: '#1e293b' }}>{summary.total}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Uploaded</Typography>
                            <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 800, color: '#1e40af' }}>{summary.uploaded}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Reviewed</Typography>
                            <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 800, color: '#6d28d9' }}>{summary.reviewed}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={2}>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Approved</Typography>
                            <Typography sx={{ fontFamily, fontSize: '1.5rem', fontWeight: 800, color: '#166534' }}>{summary.approved}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, mb: 0.5 }}>
                                Completion: {summary.completion}%
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={summary.completion}
                                sx={{
                                    height: 10, borderRadius: 5,
                                    bgcolor: '#e2e8f0',
                                    '& .MuiLinearProgress-bar': { bgcolor: ACCENT, borderRadius: 5 },
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* Category counts */}
                    {summary.by_category && summary.by_category.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                            {summary.by_category.map((cat, i) => (
                                <Chip key={i} label={`${getStatusLabel(cat.category)}: ${cat.count}`} size="small"
                                    sx={{
                                        fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                        bgcolor: (CATEGORY_COLORS[cat.category] || CATEGORY_COLORS.OTHER).bg,
                                        color: (CATEGORY_COLORS[cat.category] || CATEGORY_COLORS.OTHER).color,
                                    }} />
                            ))}
                        </Box>
                    )}
                </Card>

                {/* Filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField
                        size="small" label="Tax Year" type="number" value={taxYear}
                        onChange={e => { setTaxYear(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                        sx={{ minWidth: 110 }}
                    />
                    <TextField
                        size="small" select label="Category" value={category}
                        onChange={e => { setCategory(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 160 }}
                    >
                        {CATEGORIES.map(c => <MenuItem key={c.value} value={c.value} sx={{ fontFamily }}>{c.label}</MenuItem>)}
                    </TextField>
                    <TextField
                        size="small" select label="Status" value={status}
                        onChange={e => { setStatus(e.target.value); setPage(0); }}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }}
                        sx={{ minWidth: 140 }}
                    >
                        {STATUSES.map(s => <MenuItem key={s.value} value={s.value} sx={{ fontFamily }}>{s.label}</MenuItem>)}
                    </TextField>
                </Box>

                {/* Table */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Document Name', 'Category', 'Status', 'File', 'Due Date', 'Actions'].map(h => (
                                        <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map(i => (
                                        <TableRow key={i}>{[1, 2, 3, 4, 5, 6].map(j => <TableCell key={j}><Skeleton height={36} /></TableCell>)}</TableRow>
                                    ))
                                ) : documents.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography sx={{ fontFamily, color: '#94a3b8' }}>No documents found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : documents.map(doc => (
                                    <TableRow key={doc.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                        <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>{doc.document_name}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Chip label={getStatusLabel(doc.category)} size="small"
                                                sx={{
                                                    fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                    bgcolor: (CATEGORY_COLORS[doc.category] || CATEGORY_COLORS.OTHER).bg,
                                                    color: (CATEGORY_COLORS[doc.category] || CATEGORY_COLORS.OTHER).color,
                                                }} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Chip label={getStatusLabel(doc.status)} size="small"
                                                sx={{
                                                    fontFamily, fontWeight: 600, fontSize: '0.72rem',
                                                    bgcolor: (STATUS_COLORS[doc.status] || STATUS_COLORS.NEEDED).bg,
                                                    color: (STATUS_COLORS[doc.status] || STATUS_COLORS.NEEDED).color,
                                                }} />
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {doc.file_name || doc.document_url ? (
                                                doc.document_url ? (
                                                    <Typography component="a" href={doc.document_url} target="_blank" rel="noopener"
                                                        sx={{ fontFamily, fontSize: '0.85rem', color: '#1e40af', textDecoration: 'underline' }}>
                                                        {doc.file_name || 'View'}
                                                    </Typography>
                                                ) : doc.file_name
                                            ) : '\u2014'}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>{formatDate(doc.due_date)}</TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                {(doc.status === 'UPLOADED' || doc.status === 'NEEDED') && (
                                                    <Tooltip title="Review">
                                                        <IconButton size="small" onClick={() => openReview(doc)}>
                                                            <FactCheckIcon sx={{ fontSize: 18, color: '#6d28d9' }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Edit">
                                                    <IconButton size="small" onClick={() => openEditForm(doc)}>
                                                        <EditIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" onClick={() => handleDelete(doc.id)}>
                                                        <DeleteIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div" count={total} page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 20, 50]}
                        sx={{ '& *': { fontFamily } }}
                    />
                </Card>
            </Box>

            {/* Add/Edit Document Dialog */}
            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>{editingId ? 'Edit Document' : 'Add Document'}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Document Name" required
                                value={formData.document_name}
                                onChange={e => setFormData(prev => ({ ...prev, document_name: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth select label="Category" required
                                value={formData.category}
                                onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}>
                                {CATEGORIES.filter(c => c.value).map(c => (
                                    <MenuItem key={c.value} value={c.value} sx={{ fontFamily }}>{c.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Due Date" type="date"
                                value={formData.due_date}
                                onChange={e => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                                InputLabelProps={{ shrink: true, sx: { fontFamily } }}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth label="Description" multiline rows={2}
                                value={formData.description}
                                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="File Name"
                                value={formData.file_name}
                                onChange={e => setFormData(prev => ({ ...prev, file_name: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }}
                                placeholder="e.g. W2_2025.pdf" />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth label="Document URL"
                                value={formData.document_url}
                                onChange={e => setFormData(prev => ({ ...prev, document_url: e.target.value }))}
                                InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                                InputLabelProps={{ sx: { fontFamily } }} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setFormOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleSaveDocument} variant="contained" disabled={saving || !formData.document_name}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Review Dialog */}
            <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Review Document</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#475569', mb: 2 }}>
                        Mark this document as reviewed. Optionally add reviewer notes.
                    </Typography>
                    <TextField size="small" fullWidth label="Reviewer Notes" multiline rows={3}
                        value={reviewerNotes}
                        onChange={e => setReviewerNotes(e.target.value)}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setReviewOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleReview} variant="contained" disabled={reviewing}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#6d28d9', '&:hover': { bgcolor: '#5b21b6' } }}>
                        {reviewing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirm Review'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Generate Checklist Dialog */}
            <Dialog open={checklistOpen} onClose={() => setChecklistOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Generate Document Checklist</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#475569', mb: 2 }}>
                        Auto-generate a checklist of required documents for the selected tax year.
                    </Typography>
                    <TextField size="small" fullWidth label="Tax Year" type="number"
                        value={checklistYear}
                        onChange={e => setChecklistYear(e.target.value)}
                        InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                        InputLabelProps={{ sx: { fontFamily } }} />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setChecklistOpen(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleGenerateChecklist} variant="contained" disabled={generating || !checklistYear}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}>
                        {generating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Generate'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
